/**
 * ResetSelfPasswordDialog — Centered modal for changing your own password.
 *
 * Unlike the admin ResetPasswordDialog (which only needs new + confirm),
 * this dialog also requires the user's CURRENT password for verification.
 * The backend action is "reset-self" rather than "reset".
 *
 * @param {{
 *   username: string,
 *   onClose: () => void,
 *   onConfirm: (data: { currentPassword, password }) => Promise<[boolean, string?]>,
 * }} props
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import {
  PASSWORD_MIN_LENGTH, PASSWORD_PATTERN_ATTR, PASSWORD_RULE_HINT, ADMIN_WEAK_PASSWORD_MSG,
  isPasswordFormatValid, isAdminWeakPassword as isWeakForRole,
} from '../../../../utils/passwordRules.js';

const ResetSelfPasswordDialog = ({ username, roleId, onClose, onConfirm }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const inputRef = useRef(null);

  // Focus the current password field on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Validation
  const currentPasswordEmpty = currentPassword.length === 0;
  const newPasswordFormatBad = newPassword.length > 0 && !isPasswordFormatValid(newPassword);
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  // Same "123456" rule as every other password screen, applied to the signed-in user.
  const weakForMyRole = isWeakForRole(newPassword, roleId);

  const isValid = useMemo(() => {
    if (!currentPassword) return false;
    if (!isPasswordFormatValid(newPassword)) return false;
    if (newPassword !== confirmPassword) return false;
    if (weakForMyRole) return false;
    return true;
  }, [currentPassword, newPassword, confirmPassword, weakForMyRole]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isValid || saving) return;
      setSaving(true);
      setApiError('');

      try {
        const result = await onConfirm({
          currentPassword,
          password: newPassword,
        });
        if (!result[0]) {
          // Map error codes to user-friendly messages
          const code = result[1];
          let msg;
          if (code === '6' || code === '3' || code === '4') {
            msg = 'Current password is incorrect.';
          } else {
            msg = result[1] || 'Password reset failed. Please try again.';
          }
          setApiError(msg);
          setSaving(false);
        }
        // On success the orchestrator will close the dialog & show toast
      } catch {
        setApiError('An unexpected error occurred.');
        setSaving(false);
      }
    },
    [isValid, saving, currentPassword, newPassword, onConfirm]
  );

  return (
    <div className="pp-dialog-backdrop" onClick={onClose}>
      <div
        className="pp-dialog pp-dialog--sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reset Password"
      >
        {/* Header */}
        <div className="pp-dialog__header pp-dialog__header--top">
          <div>
            <h2 className="pp-dialog__title">Change Password</h2>
            <p className="pp-dialog__subtitle">{username}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close dialog"
          >
            <svg className="mpp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="pp-dialog__body pp-dialog__body--stack">
            {apiError ? (
              <div className="pp-dialog__error-banner">
                <svg className="mpp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span>{apiError}</span>
              </div>
            ) : null}

            <div>
              <label htmlFor="mpp-current-password" className="mpp-field__label">
                Current Password <span className="mpp-field__required">*</span>
              </label>
              <input
                ref={inputRef}
                type="password"
                id="mpp-current-password"
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setApiError(''); }}
                className="mpp-field__input"
                placeholder="Enter current password"
                autoComplete="current-password"
                disabled={saving}
              />
            </div>

            <div>
              <label htmlFor="mpp-new-password" className="mpp-field__label">
                New Password <span className="mpp-field__required">*</span>
              </label>
              <input
                type="password"
                id="mpp-new-password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setApiError(''); }}
                className="mpp-field__input"
                placeholder="Minimum 6 characters, letters and numbers only"
                autoComplete="new-password"
                disabled={saving}
                minLength={PASSWORD_MIN_LENGTH}
                pattern={PASSWORD_PATTERN_ATTR}
                title={PASSWORD_RULE_HINT}
              />
              {newPasswordFormatBad ? (
                <p className="mpp-field__error">{PASSWORD_RULE_HINT}</p>
              ) : (
                <p className="mpp-field__hint">{PASSWORD_RULE_HINT}</p>
              )}
              {weakForMyRole ? (
                <p className="mpp-field__error">{ADMIN_WEAK_PASSWORD_MSG}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="mpp-confirm-password" className="mpp-field__label">
                Confirm New Password <span className="mpp-field__required">*</span>
              </label>
              <input
                type="password"
                id="mpp-confirm-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setApiError(''); }}
                className="mpp-field__input"
                placeholder="Re-enter new password"
                autoComplete="new-password"
                disabled={saving}
              />
              {passwordsMismatch ? (
                <p className="mpp-field__error">Passwords do not match.</p>
              ) : null}
            </div>
          </div>

          {/* Footer */}
          <div className="pp-dialog__footer">
            <button
              type="button"
              onClick={onClose}
              className="mpp-btn mpp-btn--cancel"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || saving}
              className="mpp-btn mpp-btn--primary"
            >
              {saving ? (
                <span className="mpp-saving-text">
                  <svg className="mpp-icon-md mpp-spinner" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                'Change Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetSelfPasswordDialog;
